import React, { FC, useContext, useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../../context/auth';
import useMoviePurchaseMutation from '../../../../pages/movies/movieDetails/mutation/useMoviePurchaseMutation';
import { useUserQuery } from '../../../../hooks/query';
import { takeContractId } from '../../../../utils/refetch';
import BaseModal from '../BaseModal/BaseModal';
import styles from './PinConfirmationModal.module.scss';
import parse from 'html-react-parser';

type Props = {
  centered: boolean;
  show: boolean;
  selectedButton: string;
  onHide: () => void;
  handleSuccess: (purchaseData?: any) => void;
  streamLimitReached?: boolean;
  movieId: string;
  movieTitle: string;
};

const PinConfirmationModal: FC<Props> = ({
                                           show,
                                           onHide,
                                           handleSuccess,
                                           selectedButton,
                                           movieId,
                                           movieTitle,
                                         }) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [validationTriggered, setValidationTriggered] = useState<boolean>(false);
  const authContext = useContext(AuthContext);
  const { userLocation } = authContext;
  useUserQuery(true, takeContractId() != null);

  const { mutate, isLoading } = useMoviePurchaseMutation(movieId);

  // Clear pin state when modal opens
  useEffect(() => {
    if (show) {
      setPin('');
      setError(null);
      setValidationTriggered(false);
    }
  }, [show]);

  const getUserCountry = () => {
    return localStorage.getItem('country_id');
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 4) {
      setPin(value);
    }
  };

  const handleBlur = () => {
    setValidationTriggered(true);
  };

  const handleCheckPin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setValidationTriggered(true);
    if (pin.length < 4) {
      return;
    }

    let purchaseData: any = {};
    
    if (selectedButton === 'hd') {
      purchaseData = {
        license_type: 'download-hd',
        content_name: movieTitle,
      };
    } else if (selectedButton === 'sd') {
      purchaseData = {
        license_type: 'download',
        content_name: movieTitle,
      };
    } else if (selectedButton === 'play') {
      purchaseData = {
        license_type: 'stream',
        content_name: movieTitle,
      };
    }
    
    const payload = {
      ...purchaseData,
      pin: pin,
      contract_id: takeContractId(),
      username: localStorage.getItem('username') || ''
    };

    try {
      mutate(
        payload,
        {
          onSuccess(data: any) {
            if (data.status === 'success') {
              setPin('');
              setError('');
              setValidationTriggered(false);
              
              if (selectedButton === 'sd' || selectedButton === 'hd') {
                handleSuccess(); // Call without data for downloads
                setTimeout(function () {
                  window.location.replace(data.data.url);
                }, 500);
              } else {
                handleSuccess(data);
              }
            }
          },
          onError(error: any) {
            setError(error?.body.message);
            setPin('');
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleHide = () => {
    setPin('');
    setError('');
    setValidationTriggered(false);
    onHide();
  };

  return (
    <BaseModal
      open={show}
      onClose={handleHide}
      title={`${getUserCountry() === 'AT' ? 'Sky' : '18+'} PIN`}
      actions={
        <Box display="flex" justifyContent="center" width="100%">
          <Button
            variant="contained"
            onClick={handleCheckPin}
            disabled={pin?.length < 4 || isLoading}
            size="large"
            className="primary"
            sx={{ color: 'white' }}
          >
{isLoading ? 'ÜBERPRÜFUNG...' : 'ABSENDEN'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ mt: 2 }}>
        {/* Content Warning */}
        {(selectedButton === 'hd' || selectedButton === 'sd') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
              {t('pinConfirmationModal.downloadWarning')}
            </Typography>
          </Alert>
        )}

        {/* PIN Description */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          Gib deine PIN ein, um diesen Film zu sehen.
        </Typography>

        {/* PIN Input */}
        <TextField
          name="pin"
          label={getUserCountry() === 'AT' ? 'Sky PIN' : '18+ PIN'}
          slotProps={{
            input: {
              inputProps: {
                maxLength: 4
              }
            }
          }}
          value={pin}
          onChange={handlePinChange}
          onBlur={handleBlur}
          type="password"
          required
          autoFocus
          focused
          fullWidth
          variant="outlined"
          margin="dense"
          error={validationTriggered && pin.length > 0 && pin.length < 4}
          helperText={validationTriggered && pin.length > 0 && pin.length < 4 ? 'PIN muss 4 Ziffern haben' : ''}
          sx={{
            maxWidth: 'none !important',
            width: '100%',
            '&.MuiTextField-root': {
              maxWidth: 'none !important'
            }
          }}
        />

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {parse(String(error))}
          </Alert>
        )}
      </Box>
    </BaseModal>
  );
};

export default PinConfirmationModal;
